import React, { useEffect, useRef, useState } from "react";
import InputBox from "src/components/atoms/v2/inputBox/inputBox";
import { ILoggedInUser, useAuth } from "../auth/auth-context";
import { AccountViewModel } from "./account-selection-view-model";
import LoadingModal from "src/components/molecules/loading/LoadingModal";
import { useNavigate } from "react-router-dom";
import { refreshTokens } from "../auth";
import { IAccountModel } from "../account-management/account-management-view-model";
import { TokenHelper } from "../auth/token-helper";
import { PermissionBagService } from "../auth/permissionBagService";
import ButtonPro from "src/components/atoms/primary-button/primary-button";
import Logo from "src/components/molecules/logo/Logo";
import LogoutLinkButton from "src/components/molecules/logout-button/logout-link-button";
import ButtonWithIcon from "src/components/atoms/button-with-icon/button-with-icon";
import { ChevronLeft, ChevronRight, UserIcon } from "lucide-react";

const AccountSelection = () => {
	const { user, login } = useAuth();
	const [filterValue, setFilterValue] = useState("");
	const [accounts, setAccounts] = useState<IAccountModel[]>([]);
	const [filteredAccounts, setFilteredAccounts] = useState<IAccountModel[]>(
		[]
	);
	const accountViewModel = useRef<AccountViewModel | null>(null);
	const [loadingAccounts, setLoadingAccounts] = useState(false);
	const navigate = useNavigate();
	const tokenHelper = useRef<TokenHelper | null>(null);
	const [canCreateNewAccount, setCanCreateNewAccount] = useState(false);
	const [canCreateResellerAccount, setCanCreateResellerAccount] =
		useState(false);
	const [canSeedData, setCanSeedData] = useState(false);
	const recordsPerPage = 5; // Number of records per page

	useEffect(() => {
		if (!user) {
			navigate("/login");
		} else {
			usePermissions();
			if (user && user.accessToken) {
				tokenHelper.current = new TokenHelper();
				accountViewModel.current = new AccountViewModel(
					user.accessToken
				);

				performRefreshSession();
			}
		}
	}, []);

	useEffect(() => {
		if (user && user.accessToken) {
			accountViewModel.current = new AccountViewModel(user?.accessToken);
			usePermissions();
			fetchAccountsAsync();
		}
	}, [user]);

	useEffect(() => {
		setFilteredAccounts(
			accounts.filter((account) =>
				filterValue.trim() === "" // If filterValue is empty, show all accounts
					? true
					: account.name
							.toLowerCase()
							.includes(filterValue.toLowerCase())
			)
		);
	}, [accounts, filterValue]);

	const usePermissions = () => {
		if (user) {
			setCanCreateNewAccount(
				user?.permissionBag.permissions.some(
					(p) => p.name === "Account" && p.value === "Create"
				)
			);
			setCanCreateResellerAccount(
				user?.permissionBag.permissions.some(
					(p) => p.name === "Reseller" && p.value === "Create"
				)
			);
			setCanSeedData(
				user?.permissionBag.permissions.some(
					(p) => p.name === "SystemData" && p.value === "Create"
				)
			);
		}
	};

	const performRefreshSession = async () => {
		try {
			setLoadingAccounts(true);
			const tokenRefreshed = await refreshSessionAsync("");
			if (tokenRefreshed) {
				if (
					user &&
					user?.permissionBag.permissions.filter(
						(p) => p.name === "Account" && p.value === "List"
					).length > 0
				) {
					await fetchAccountsAsync(); // Execute only after refreshSessionAsync is done
					setLoadingAccounts(false);
				}
			} // Wait for this to complete
		} catch (error) {
			console.error(
				"Error while refreshing session or fetching accounts:",
				error
			);
		}
	};

	const refreshSessionAsync = async (accountId: string): Promise<boolean> => {
		if (accountViewModel.current) {
			setLoadingAccounts(true);
			const result =
				await accountViewModel.current.setSessionAsync(accountId);

			if (result) {
				try {
					await new Promise((resolve, reject) => {
						refreshTokens(async (err, tokenResult) => {
							if (tokenResult) {
								try {
									const decodedIdToken =
										await tokenHelper.current?.decodeToken(
											tokenResult.idToken
										);
									const decodedAccessToken =
										await tokenHelper.current?.decodeToken(
											tokenResult.accessToken
										);

									if (decodedAccessToken) {
										const permissionService =
											new PermissionBagService(
												tokenResult.accessToken
											);
										const permissions =
											await permissionService.getPermissionBag(
												decodedAccessToken?.role ?? ""
											);

										const loggedInUser: ILoggedInUser = {
											id: decodedAccessToken.sub,
											givenName:
												decodedAccessToken.given_name,
											familyName:
												decodedAccessToken.family_name,
											username: decodedAccessToken.email,
											phoneNumber: `${decodedIdToken?.phone_number ?? 0}`,
											ex_user_id:
												decodedAccessToken.username,
											accessToken:
												tokenResult.accessToken,
											accountId:
												decodedAccessToken.account_id ??
												"",
											email:
												decodedAccessToken.email ?? "",
											role: decodedAccessToken.role,
											exp: decodedAccessToken.exp,
											reseller_id:
												decodedAccessToken.reseller_id,
											permissionBag: permissions
												? permissions
												: { permissions: [] },
											has_multiple_accounts:
												decodedAccessToken.has_multiple_accounts,
										};

										login(loggedInUser); // Directly log in the user
										// setLoading(false);
										// setLoadingAccounts(false);
										resolve(true); // Resolve the promise to indicate success
									} else {
										// setLoading(false);
										setLoadingAccounts(false);
										navigate("/error");
										reject(
											new Error(
												"Access token decoding failed"
											)
										);
									}
								} catch (decodeError) {
									console.error(
										"Error decoding tokens:",
										decodeError
									);
									reject(decodeError);
								}
							} else {
								// setLoading(false);
								setLoadingAccounts(false);
								console.error("Failed to refresh tokens:", err);
								reject(err);
							}
						});
					});
					return true; // Return true if everything succeeds
				} catch (error) {
					console.error("Error in refreshSessionAsync:", error);
					return false; // Return false if an error occurs
				}
			}
		}
		return false; // Return false if accountViewModel.current is null or setSessionAsync fails
	};

	const fetchAccountsAsync = async () => {
		setLoadingAccounts(true);
		if (user && user.accessToken && accountViewModel.current) {
			if (
				user.permissionBag.permissions.filter(
					(p) => p.name === "Account" && p.value === "List"
				)
			) {
				const response =
					await accountViewModel.current.getAccountsAsync();

				if (response) {
					setAccounts(response);
				}
			}

			setLoadingAccounts(false);
		}
	};

	const handleAccountSelection = async (account: IAccountModel) => {
		if (accountViewModel.current) {
			try {
				setLoadingAccounts(true);
				const tokenRefreshed = await refreshSessionAsync(
					account.accountId
				);

				if (tokenRefreshed) {
					navigate("/");
				} else {
					navigate("/error");
				}
			} catch (error) {
				console.error("Error in account selection:", error);
			} finally {
				setLoadingAccounts(false);
			}
		}
	};

	const [currentPage, setCurrentPage] = useState(1);

	// Calculate total pages
	const totalPages = Math.ceil(filteredAccounts.length / recordsPerPage);

	// Get the records for the current page
	const paginatedAccounts = filteredAccounts.slice(
		(currentPage - 1) * recordsPerPage,
		currentPage * recordsPerPage
	);

	// Handlers for pagination
	const nextPage = () =>
		setCurrentPage((prev) => Math.min(prev + 1, totalPages));
	const prevPage = () => setCurrentPage((prev) => Math.max(prev - 1, 1));

	return (
		<div className="bg-gradient-to-r from-gray-700 to-gray-900 h-screen p-6 flex flex-col items-center justify-center overflow-x-auto w-full">
			<div className="bg-gray-200 flex flex-col items-start justify-center overflow-x-auto gap-4 p-6 px-12 drop-shadow-2xl mx-auto w-[1200px]">
				<div className="flex items-center justify-end gap-4 w-full">
					<div className="flex items-center justify-center gap-1">
						<UserIcon></UserIcon>
						<span className="font-Poppins font-semibold text-gray-800 text-center">
							{user?.givenName} {user?.familyName}
						</span>
					</div>
					<LogoutLinkButton />
				</div>
				<Logo />
				<span className="font-Poppins font-semibold text-gray-800 text-center w-full text-2xl">
					Available accounts
				</span>

				{accounts.length > 1 && (
					<InputBox
						label={"Filter"}
						type={"text"}
						value={filterValue}
						onChange={(value) => setFilterValue(value)}
					/>
				)}

				<LoadingModal
					open={filteredAccounts.length <= 0 && loadingAccounts}
				/>

				{/* Accounts List */}
				<div className="flex flex-col items-start justify-center gap-2 w-full">
					{paginatedAccounts
						.sort((a, b) =>
							a.productPlan.localeCompare(b.productPlan)
						)
						.map((account) => (
							<div
								key={account.accountId}
								onClick={() => handleAccountSelection(account)}
								className={`cursor-pointer flex items-center justify-between gap-2 w-full p-2 group border border-dotted bg-gradient-to-r from-gray-50 tp-gray-100 to-white hover:from-gray-500 hover:to-gray-300 text-gray-800 hover:text-white`}
							>
								<div className="flex items-center justify-center gap-2">
									<span
										className={`border-2 font-Poppins font-semibold text-2xl text-white px-2
									${
										account.productPlan === "Demo"
											? "bg-orange-500"
											: account.productPlan === "Basic"
												? "bg-green-700"
												: account.productPlan ===
													  "Quick"
													? "bg-yellow-500"
													: account.productPlan ===
														  "Advanced"
														? "bg-purple-500"
														: ""
									}
								`}
									>
										{account.productPlan.charAt(0)}
									</span>
									<div className="flex flex-col items-center justify-center">
										<span
											className={`font-Poppins font-medium text-xl w-full`}
										>
											{account.name}
										</span>
										<span
											className={`font-Poppins font-normal text-sm w-full tracking-wider`}
										>
											{account.rootUser.firstName}{" "}
											{account.rootUser.lastName}
											{" | "}
											{account.rootUser.emailId}
										</span>
									</div>
								</div>

								<ButtonWithIcon
									icon={
										<svg
											xmlns="http://www.w3.org/2000/svg"
											viewBox="0 0 24 24"
											fill="currentColor"
											className="size-8 text-gray-600"
										>
											<path
												fillRule="evenodd"
												d="M12 2.25c-5.385 0-9.75 4.365-9.75 9.75s4.365 9.75 9.75 9.75 9.75-4.365 9.75-9.75S17.385 2.25 12 2.25Zm4.28 10.28a.75.75 0 0 0 0-1.06l-3-3a.75.75 0 1 0-1.06 1.06l1.72 1.72H8.25a.75.75 0 0 0 0 1.5h5.69l-1.72 1.72a.75.75 0 1 0 1.06 1.06l3-3Z"
												clipRule="evenodd"
											/>
										</svg>
									}
									onClick={() =>
										handleAccountSelection(account)
									}
								/>
							</div>
						))}
				</div>

				{/* Pagination Controls */}
				{totalPages > 1 && (
					<div className="flex items-center justify-center gap-4 w-full mt-4">
						<button
							onClick={prevPage}
							disabled={currentPage === 1}
							className={`font-Poppins font-semibold px-2 py-2  text-gray-800 rounded ${currentPage === 1 ? "opacity-50 cursor-not-allowed" : "hover:bg-gray-200"}`}
						>
							<ChevronLeft size={32}></ChevronLeft>
						</button>
						<span className="font-Poppins font-semibold text-gray-800">
							Page {currentPage} of {totalPages}
						</span>
						<button
							onClick={nextPage}
							disabled={currentPage === totalPages}
							className={`font-Poppins font-semibold px-2 py-2  text-gray-800 rounded ${currentPage === totalPages ? "opacity-50 cursor-not-allowed" : "hover:bg-gray-200"}`}
						>
							<ChevronRight size={32}></ChevronRight>
						</button>
					</div>
				)}

				{/* Other Buttons */}
				<div className="flex items-center justify-center gap-4 w-full mt-12">
					{/* {canSeedData && (
						<div className="flex flex-col items-center justify-center gap-4">
							<ButtonPro
								onClick={() => navigate("/seed-data")}
								label={"Seed data"}
								disabled={!canSeedData}
							/>
						</div>
					)} */}
					{canCreateNewAccount && (
						<div className="flex flex-col items-center justify-center gap-4">
							<ButtonPro
								onClick={() => navigate("/create-account")}
								label={`Create account`}
								disabled={!user || !user?.reseller_id}
							/>
						</div>
					)}

					{canCreateResellerAccount && (
						<div className="flex flex-col items-center justify-center gap-4">
							<ButtonPro
								onClick={() => navigate("/create-reseller")}
								label={"Create reseller"}
							/>
						</div>
					)}
				</div>
			</div>
		</div>
	);
};

export default AccountSelection;
